<template>
    <two-column-layout>
        <card-container>
            <h2>{{ $t(i18n.additional_risks_step.title) }}</h2>
            <p>{{ $t(i18n.additional_risks_step.description) }}</p>

            <radio-group-fragment
              key="insureFinancialDamage"
              :label="$t(i18n.input.insure_financial_damage.label)"
              :description="$t(i18n.input.insure_financial_damage.description)"
              group-name="group company profile insure financial damage"
              :options="radioGroupYesNoOptions"
              show-info
              v-model="form.insureFinancialDamage"
              :is-invalid="isFieldInvalid($v.form.insureFinancialDamage)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_INSURE_FINANCIAL_DAMAGE')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.insureFinancialDamage" />
                </template>
            </radio-group-fragment>

            <currency-input-fragment v-if="isFieldAnsweredPositively(form.insureFinancialDamage)"
              :label="$t(i18n.input.financial_burden.label)"
              :description="$t(i18n.input.financial_burden.description)"
              size="small"
              show-info
              v-model="form.financialBurden"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.financialBurden)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_FINANCIAL_BURDEN')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.financialBurden" />
                </template>
            </currency-input-fragment>

            <fragment-separator></fragment-separator>

            <radio-group-fragment
              key="legalAssistance"
              :label="$t(i18n.input.legal_assistance.label)"
              :description="$t(i18n.input.legal_assistance.description)"
              group-name="group company profile legal assistance"
              :options="radioGroupYesNoOptions"
              show-info
              v-model="form.legalAssistance"
              :is-invalid="isFieldInvalid($v.form.legalAssistance)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_LEGAL_ASSISTANCE')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.legalAssistance" />
                </template>
            </radio-group-fragment>

            <fragment-separator></fragment-separator>

            <text-area-fragment
              :label="$t(i18n.input.remarks.label)"
              :description="$t(i18n.input.remarks.description)"
              show-info
              optional
              v-model="form.remarks"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_REMARKS')"
            ></text-area-fragment>

        </card-container>
        <button-group direction="right" top-spacing="l">
            <button-fragment icon-before="back"
                             as-link
                             @click="previousStep()">{{$t(i18n.button.previous)}}
            </button-fragment>
            <button-fragment @click="nextStep()">{{$t(i18n.button.next)}}</button-fragment>
        </button-group>
    </two-column-layout>
</template>

<script>
    import {Utils} from "@bvm/core";
    import {
        RadioGroupFragment,
        TextAreaFragment,
        CurrencyInputFragment,
        FragmentSeparator,
        TwoColumnLayout,
        ButtonFragment,
        ButtonGroup,
        CardContainer
    } from "@bvm/forms";
    import {
        minValue,
        maxValue,
        required,
        decimal,
        requiredIf
    } from 'vuelidate/lib/validators'
    import FieldErrorMessages from "../../shared/field-error-messages";
    import MediaItemModal from "../../shared/media-item-modal";
    import {createNamespacedHelpers} from "vuex";

    const { mapState, mapMutations } = createNamespacedHelpers(
      "gpsQuoteRequest"
    );

    const i18nBuilder = new Utils.I18n.I18nBuilder("gps_quote_request_form")
      .add("additional_risks_step", b => b.add("title").add("description"))
      .add("button", b => b.add("previous").add("next"))
      .add("radio_group", b => b.add("generic", c => c.add("yes").add("no")))
      .add("input", b => b
        .add("financial_burden", c => c.add("label").add("description"))
        .add("remarks", c => c.add("label").add("description"))
        .add("insure_financial_damage", c => c.add("label").add("description"))
        .add("legal_assistance", c => c.add("label").add("description")));

    const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
      .add("minValue")
      .add("maxValue")
      .add("decimal")
      .add("required");

    export const messageKeys = [...i18nBuilder.getKeys(), ...i18nValidationBuilder.getKeys()];

    export default {
        name: "additional-risks-step",
        components: {
            FieldErrorMessages,
            RadioGroupFragment,
            TextAreaFragment,
            CurrencyInputFragment,
            FragmentSeparator,
            TwoColumnLayout,
            ButtonFragment,
            ButtonGroup,
            CardContainer
        },
        props: {},
        data() {
            return {
                i18n: i18nBuilder.getData(),
                form : {
                    insureFinancialDamage : null,
                    legalAssistance : null,
                    financialBurden : null,
                    remarks : null
                }
            };
        },
        computed: {
            ...mapState(["additionalRisks", "companyProfile", "companyInfo"]),
            radioGroupYesNoOptions() {
                return {
                    y: this.$t(this.i18n.radio_group.generic.yes),
                    n: this.$t(this.i18n.radio_group.generic.no)
                };
            }
        },
        methods: {
            ...mapMutations(["setAdditionalRisks"]),
            isFieldInvalid(field) {
                return field.$dirty && field.$invalid;
            },
            isFieldAnsweredPositively(fieldValue) {
                return fieldValue === 'y';
            },
            onInfoClicked(mediaItemTag) {
                this.$bvmModal.show(MediaItemModal, {
                    itemTag: mediaItemTag
                }, {
                    clickToClose: true
                });
            },
            updateState() {
                if (!this.isFieldAnsweredPositively(this.form.insureFinancialDamage)) {
                    this.form.financialBurden = null;
                }
                this.setAdditionalRisks(this.form);
            },
            previousStep(value) {
                this.$emit("previousStep", value);
            },
            nextStep() {
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$emit("onError");
                } else {
                    this.updateState();
                    this.$emit("nextStep");
                }
            }
        },
        created() {
            this.form = this.additionalRisks;
        },
        validations() {
            return {
                form : {
                    insureFinancialDamage : { required },
                    legalAssistance : { required },
                    financialBurden : {
                        required: requiredIf(() => this.isFieldAnsweredPositively(this.form.insureFinancialDamage)),
                        minValue: minValue(0),
                        maxValue: maxValue(100000000), //100_000_000
                        decimal
                    }
                }
            }
        }
    }
</script>

<style>

</style>